/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import {
  Banner,
  Button,
  Card,
  Icon,
  Typography,
} from '@equinor/eds-core-react';
import { CenteredLoader } from '../../../components/CenteredLoader/CenteredLoader';
import { error_outlined } from '@equinor/eds-icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { startTransition, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import {
  AnalogueModelDetail,
  putApiV1AnalogueModelsById,
  UpdateAnalogueModelCommandBody,
} from '../../../api/generated';
import { queryKeys } from '../../../hooks/queryKeys';
import { GrossDepositionEnviromentGroup } from '../../../components/GrossDepositionEnviroment/GrossDepositionEnviromentGroup/GrossDepositionEnviromentGroup';
import { OutcropAnalogueGroup } from '../../../components/OutcropAnalogue/OutcropAnalogueGroup/OutcropAnalogueGroup';
import { StratigrapicGroups } from '../../../components/StrategraphicColumn/StratigrapicGroups/StratigrapicGroups';
import { EditNameDescription } from '../EditNameDescription/EditNameDescription';
import * as Styled from './ModelMetadataView.styled';
import { useIsOwnerOrAdmin } from '../../../hooks/useIsOwnerOrAdmin';
import { analogueModelDefault } from '../../../constants/analogueModelDefault';
import { useJobStore } from '../../../stores/JobStore';
import { useFetchModel } from '../../../hooks/useFetchModel';

import { IniParametersDialog } from '../IniParametersDialog/IniParametersDialog';
import { ModelStatus } from '../../ModelTable/ModelTable';
import { UploadingStatus } from '../../../pages/AddModel/stores/AddModelStore';
import { ModelAreaCoordinates } from '../ModelAreaCoordinates/ModelAreaCoordinates';
import { CoordinatesDialog } from '../../AreaCoordinates/CoordinatesDialog/CoordinatesDialog';
import { ModelFilesView } from '../ModelFilesView/ModelFilesView';
import { ModelNameFrameDetail } from '../ModelNameFrame/ModelNameFrameDetail';
import { ModelArchelMap } from '../ModelArchelMap/ModelArchelMap';
import { useFetchJobStatus } from '../../../hooks/useFetchJobStatus';
import * as StyledCard from '../../../styles/Card/Card.styled';
import { ModelImageDisplay } from '../ModelAreaCoordinates/ModelImageDisplay/ModelImageDisplay';

/* eslint-disable camelcase */
Icon.add({ error_outlined });

export const ModelMetadataView = ({
  uploadingStatus,
}: {
  uploadingStatus?: UploadingStatus;
}) => {
  const { modelId } = useParams();
  const queryClient = useQueryClient();
  const isOwnerOrAdmin = useIsOwnerOrAdmin();
  const { data: analogueModel = analogueModelDefault } = useFetchModel();
  const { conversionJobId, setConversionJobId } = useJobStore(
    useShallow((s) => ({
      conversionJobId: s.conversionJobId,
      setConversionJobId: s.setConversionJobId,
    })),
  );
  const [open, setOpen] = useState<boolean>(false);

  const [isAddModelDialog, setAddModelDialog] = useState<boolean>(false);

  // Defer mounting ModelImageDisplay so the page shell paints immediately
  const [showCanvas, setShowCanvas] = useState(false);
  useEffect(() => {
    startTransition(() => {
      setShowCanvas(true);
    });
  }, []);

  const checkConversionStatus = useFetchJobStatus(conversionJobId);

  const toggleOpen = () => {
    setOpen(!open);
  };

  useEffect(() => {
    if (
      analogueModel !== analogueModelDefault &&
      !analogueModel.isProcessed &&
      checkConversionStatus.data?.data?.data.jobStatus === 'Succeeded'
    ) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.analogueModel(modelId as string),
      });
      setConversionJobId(undefined);
    }
  }, [
    analogueModel,
    checkConversionStatus.data?.data?.data.jobStatus,
    modelId,
    queryClient,
    setConversionJobId,
  ]);

  function toggleEditMetadata() {
    setAddModelDialog(!isAddModelDialog);
  }

  const updateNameDescription = useMutation({
    mutationKey: ['analogue-model'],
    mutationFn: ({
      id,
      requestBody,
    }: {
      id: string;
      requestBody: UpdateAnalogueModelCommandBody;
    }) => {
      return putApiV1AnalogueModelsById({
        body: requestBody,
        path: { id: id },
      });
    },
  });

  const updateModelMetadata = async (metadata: AnalogueModelDetail) => {
    const id = analogueModel.analogueModelId
      ? analogueModel.analogueModelId
      : '';
    const modelMetadata = {
      name: metadata.name,
      description: metadata.description,
      sourceType: 'Deltares',
    };

    await updateNameDescription.mutateAsync({
      id: id,
      requestBody: modelMetadata,
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.analogueModel(modelId as string),
    });
    toggleEditMetadata();
  };

  const getModelStatus = () => {
    let status = ModelStatus.UNKNOWN;

    const transforming =
      analogueModel && analogueModel.processingStatus
        ? analogueModel.processingStatus
        : undefined;
    const isProcessed = analogueModel ? analogueModel.isProcessed : undefined;

    if (isProcessed === true) {
      status = ModelStatus.SUCCEEDED;
    } else if (
      transforming === 'Created' ||
      transforming === 'Waiting' ||
      transforming === 'Running'
    ) {
      status = ModelStatus.TRANSFORMING;
    } else if (transforming === 'Failed') {
      status = ModelStatus.FAILED_TRANSFORMING;
    } else if (isProcessed === false) {
      status = ModelStatus.FAILED_UPLOADING;
    }

    return status;
  };

  if (analogueModel === analogueModelDefault && uploadingStatus === undefined)
    return <CenteredLoader />;

  return (
    <>
      {getModelStatus() === ModelStatus.FAILED_TRANSFORMING && (
        <Banner className="model-error-message">
          <Banner.Icon variant="warning">
            <Icon name="error_outlined" />
          </Banner.Icon>
          <Banner.Message>
            Model transformation failed. This might be caused by an invalid ini
            file. Please delete this model and try uploading it again.
          </Banner.Message>
        </Banner>
      )}
      <Card className="card-name">
        {uploadingStatus === undefined && (
          <>
            <Card.Header>
              <ModelNameFrameDetail></ModelNameFrameDetail>
            </Card.Header>
            <StyledCard.Content>
              {analogueModel.description && (
                <Typography variant="body_long">
                  {analogueModel.description}
                </Typography>
              )}

              {isOwnerOrAdmin && (
                <Button
                  onClick={toggleEditMetadata}
                  variant="outlined"
                  className="edit-metadata-button"
                >
                  Edit name and description…
                </Button>
              )}
              <EditNameDescription
                edit={updateModelMetadata}
                isEdit={isAddModelDialog}
                closeDialog={toggleEditMetadata}
              />
            </StyledCard.Content>
          </>
        )}
      </Card>

      <ModelAreaCoordinates />

      <Card className="card-image">
        <Card.Header>
          <Typography variant="h4" as="h3" className="card-title">
            Model image
          </Typography>
        </Card.Header>
        <StyledCard.Content>
          {uploadingStatus === undefined && (
            <>
              {analogueModel.isProcessed ? (
                showCanvas ? (
                  <ModelImageDisplay />
                ) : (
                  <CenteredLoader size={48} />
                )
              ) : getModelStatus() === ModelStatus.TRANSFORMING ? (
                <CenteredLoader
                  size={48}
                  text="Your model is being transformed for further processing…"
                />
              ) : (
                <CenteredLoader
                  size={48}
                  text="The model has not yet been processed. The model image will appear here once transformation is complete."
                />
              )}
            </>
          )}
          {uploadingStatus === UploadingStatus.Uploading && (
            <Styled.UploadingMeta>
              <Typography variant="body_long">
                While your model is being uploaded, you can add some metadata
                like outcrop analogue, stratigraphic column, or gross
                depositional environment. Please note that at least one type of
                metadata is required for APP models before they can be approved
                later on.
              </Typography>
            </Styled.UploadingMeta>
          )}
        </StyledCard.Content>
      </Card>

      <Card>
        <Card.Header>
          <Typography variant="h4" as="h3" className="card-title">
            Gross Depositional Environment (GDE)
          </Typography>
        </Card.Header>
        <Card.Content>
          <GrossDepositionEnviromentGroup />
        </Card.Content>
      </Card>

      <Card>
        <Card.Header>
          <Typography variant="h4" as="h3" className="card-title">
            Architectural elements
          </Typography>
        </Card.Header>
        <Card.Content>
          <ModelArchelMap />
        </Card.Content>
      </Card>

      <Card>
        <Card.Header>
          <Typography variant="h4" as="h3" className="card-title">
            Outcrop analogue
          </Typography>
        </Card.Header>
        <Card.Content>
          <OutcropAnalogueGroup />
        </Card.Content>
      </Card>

      <Card>
        <Card.Header>
          <Typography variant="h4" as="h3" className="card-title">
            Stratigraphic column
          </Typography>
        </Card.Header>
        <Card.Content>
          <StratigrapicGroups />
        </Card.Content>
      </Card>

      <ModelFilesView />

      {analogueModel.iniParameters && uploadingStatus === undefined && (
        <Card className="card-ini">
          <Card.Header>
            <Typography variant="h4" as="h3" className="card-title">
              Ini parameters
            </Typography>
          </Card.Header>
          <StyledCard.Content>
            <Typography variant="body_long">
              All parameters added to the model in the original vendor ini file.
            </Typography>
            <IniParametersDialog iniParameters={analogueModel.iniParameters} />
          </StyledCard.Content>
        </Card>
      )}

      <CoordinatesDialog open={open} toggleOpen={toggleOpen} />
    </>
  );
};
